// Servicio para verificar permisos granulares de usuarios
import { getUser } from './auth';
import { getUserPermissions, RoomPermission } from './api';
import { isAdmin, isDemo } from './auth';

// Cache de permisos para evitar múltiples llamadas
let permissionsCache: {
  userId: number | null;
  permissions: RoomPermission[];
  timestamp: number;
} = {
  userId: null,
  permissions: [],
  timestamp: 0
};

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutos

/**
 * Obtiene los permisos del usuario actual (con cache)
 */
export const getUserRoomPermissions = async (): Promise<RoomPermission[]> => {
  const user = getUser();
  if (!user || !user.id) {
    return [];
  }

  const userIsAdmin = isAdmin(user);
  // CRITICO: Los usuarios demo ahora necesitan permisos granulares explícitos (igual que usuarios normales)
  // Solo admin tiene acceso total sin permisos granulares
  if (userIsAdmin) {
    return [];
  }

  // Verificar cache
  const now = Date.now();
  if (
    permissionsCache.userId === user.id &&
    permissionsCache.timestamp > 0 &&
    (now - permissionsCache.timestamp) < CACHE_DURATION
  ) {
    return permissionsCache.permissions;
  }

  try {
    const userPermissions = await getUserPermissions(user.id);
    const permissions: RoomPermission[] = userPermissions.permissions || [];
    
    // Actualizar cache
    permissionsCache = {
      userId: user.id,
      permissions,
      timestamp: now
    };
    
    return permissions;
  } catch (error) {
    // Mantener último cache válido del usuario (evita “sin permiso” falso en UI)
    if (permissionsCache.userId === user.id && permissionsCache.timestamp > 0) {
      return permissionsCache.permissions;
    }
    return [];
  }
};

/**
 * Verifica si el usuario puede ver estadísticas de una habitación
 */
export const canViewStatistics = async (roomId: number): Promise<boolean> => {
  const user = getUser();
  if (!user) return false;
  
  // Solo admin tiene acceso total sin permisos granulares
  if (isAdmin(user)) return true;
  
  // Demo y usuarios normales necesitan permisos granulares explícitos
  const permissions = await getUserRoomPermissions();
  const permission = permissions.find(p => p.room_id === roomId);
  return permission?.can_view_statistics || false;
};

/**
 * Verifica si el usuario puede ver una habitación y sus dispositivos
 */
export const canViewRoom = async (roomId: number): Promise<boolean> => {
  const user = getUser();
  if (!user) return false;
  
  // Solo admin tiene acceso total sin permisos granulares
  if (isAdmin(user)) return true;
  
  // Demo y usuarios normales necesitan permisos granulares explícitos
  const permissions = await getUserRoomPermissions();
  const permission = permissions.find(p => p.room_id === roomId);
  return permission?.can_view_room || false;
};

/**
 * Verifica si el usuario puede controlar dispositivos de una habitación
 */
export const canControlDevices = async (roomId: number): Promise<boolean> => {
  const user = getUser();
  if (!user) return false;
  
  // Demo no puede controlar dispositivos
  if (isDemo(user)) return false;
  
  if (isAdmin(user)) return true;
  
  const permissions = await getUserRoomPermissions();
  const permission = permissions.find(p => p.room_id === roomId);
  return permission?.can_control_devices || false;
};

/**
 * Obtiene el permiso de control de dispositivos para una habitación (síncrono desde cache)
 * Útil para verificación rápida sin async
 */
export const canControlDevicesSync = (roomId: number | null | undefined): boolean => {
  if (!roomId) return false;
  
  const user = getUser();
  if (!user) return false;
  
  // Demo no puede controlar dispositivos
  if (isDemo(user)) return false;
  
  if (isAdmin(user)) return true;
  
  // Usar cache del usuario aunque esté vencido (stale-while-revalidate).
  // Si al vencer devolvemos false, UIs como Clima marcan candado en TODAS las habitaciones
  // en cada re-render por updates de dispositivos.
  if (permissionsCache.userId === user.id && permissionsCache.timestamp > 0) {
    const permission = permissionsCache.permissions.find(p => p.room_id === roomId);
    return permission?.can_control_devices || false;
  }
  
  // Sin cache aún: no asumir denegado en UI síncrona (backend sigue siendo la fuente de verdad)
  return false;
};

/**
 * Lectura del cache de can_control_devices.
 * `null` = todavía no hay cache (no bloquear en el toque; el async decide).
 */
export const getCanControlDevicesCached = (
  roomId: number | null | undefined
): boolean | null => {
  if (!roomId) return null;

  const user = getUser();
  if (!user) return false;
  if (isDemo(user)) return false;
  if (isAdmin(user)) return true;

  if (permissionsCache.userId === user.id && permissionsCache.timestamp > 0) {
    const permission = permissionsCache.permissions.find(p => p.room_id === roomId);
    return Boolean(permission?.can_control_devices);
  }

  return null;
};

/**
 * Limpia el cache de permisos (útil cuando se actualizan permisos)
 */
export const clearPermissionsCache = () => {
  permissionsCache = {
    userId: null,
    permissions: [],
    timestamp: 0
  };
};

